// Purpose: Commit 8b.15 browser-side folder collection.
// Two entry points (DataTransferItemList from a drop event, or FileList
// from a webkitdirectory <input>) → a flat array of { file, relativePath }
// items ready for the upload pipeline.
//
// Filtering: a hard-coded noise list is dropped at collection time
// (`.DS_Store`, macOS `._*` resource forks, `Thumbs.db`, `desktop.ini`).
// Hidden files (leading `.`) other than the noise list are kept; the UI
// surfaces a warning so the user can see them in the preview.
//
// Path conventions: relativePath is `/`-joined, no leading or trailing
// slash, empty string for files at the root. The server re-sanitizes via
// sanitizeRelativePathForBlobPath; this layer is a UX convenience, not a
// security boundary.

export interface FolderEntry {
  file: File;
  relativePath: string;
}

const NOISE_FILES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

function isNoiseFile(name: string): boolean {
  if (NOISE_FILES.has(name)) return true;
  if (name.startsWith('._')) return true; // macOS resource forks
  return false;
}

// Recursively walk a FileSystemEntry tree into a flat list. Caller passes
// the parent path; the walker prepends entry names to build the full path.
async function readEntry(entry: FileSystemEntry, parentPath: string): Promise<FolderEntry[]> {
  if (entry.isFile) {
    const fsFile = entry as FileSystemFileEntry;
    if (isNoiseFile(fsFile.name)) return [];
    const file = await new Promise<File>((resolve, reject) => {
      fsFile.file(resolve, reject);
    });
    return [{ file, relativePath: parentPath }];
  }
  if (entry.isDirectory) {
    const fsDir = entry as FileSystemDirectoryEntry;
    const reader = fsDir.createReader();
    const children: FileSystemEntry[] = [];
    // readEntries returns at most ~100 entries per call on Chrome; loop
    // until the reader returns an empty batch (the documented termination).
    while (true) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      if (batch.length === 0) break;
      children.push(...batch);
    }
    const here = parentPath ? `${parentPath}/${fsDir.name}` : fsDir.name;
    const out: FolderEntry[] = [];
    for (const child of children) {
      out.push(...(await readEntry(child, here)));
    }
    return out;
  }
  return [];
}

// Drop-event entry point. Returns the full flat list of files with paths.
export async function walkDataTransferItems(items: DataTransferItemList): Promise<FolderEntry[]> {
  const out: FolderEntry[] = [];
  // Snapshot the items + entries up front. DataTransferItemList is live
  // and clears once the drop event handler returns; webkitGetAsEntry must
  // be called synchronously before any awaits.
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item || item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  for (const entry of entries) {
    out.push(...(await readEntry(entry, '')));
  }
  return out;
}

// <input webkitdirectory> entry point. Each File carries `webkitRelativePath`
// like `assets/images/hero.png`; the leaf name is at the end and we want the
// directory portion only.
export function walkWebkitDirectoryInput(files: FileList): FolderEntry[] {
  const out: FolderEntry[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (!file) continue;
    if (isNoiseFile(file.name)) continue;
    // Some browsers expose the property; falling back to '' produces the
    // flat-file behavior, which is the safe default.
    const wkPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    // Strip the leaf to get the directory portion. wkPath is always
    // `/`-joined per spec; no platform-specific separators here.
    const lastSlash = wkPath.lastIndexOf('/');
    const relativePath = lastSlash > 0 ? wkPath.slice(0, lastSlash) : '';
    out.push({ file, relativePath });
  }
  return out;
}

// Simple plain-FileList → FolderEntry[] for the existing flat-drop / file-
// input path. relativePath is always '' so callers see the same shape as
// the folder paths.
export function flatFileList(files: FileList): FolderEntry[] {
  const out: FolderEntry[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (!file) continue;
    if (isNoiseFile(file.name)) continue;
    out.push({ file, relativePath: '' });
  }
  return out;
}

// Returns true if any entry in the list represents a folder (i.e., has a
// non-empty relativePath). Used by NewShare to decide between the
// preview-then-upload and immediate-upload UX.
export function hasFolderStructure(entries: FolderEntry[]): boolean {
  return entries.some((e) => e.relativePath.length > 0);
}

// Returns the count of hidden (leading-dot, non-noise) files in the list,
// for the preview warning. The noise filter has already removed `.DS_Store`
// and friends; this is the legitimate "hidden file" count the user might
// want to know about (e.g. `.gitignore`, `.env.example`).
export function countHiddenFiles(entries: FolderEntry[]): number {
  let n = 0;
  for (const e of entries) {
    if (e.file.name.startsWith('.')) n += 1;
  }
  return n;
}
